import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import DatePicker, {
    CalendarContainer,
    DatePickerProps,
    registerLocale,
    setDefaultLocale,
} from 'react-datepicker';
import ReactDOM from 'react-dom';

import { Box, HStack, Link, useDisclosure, useSlotRecipe } from '@chakra-ui/react';

import { addMonths } from 'date-fns/addMonths';
import { differenceInDays } from 'date-fns/differenceInDays';
import { isEqual as isEqualDate } from 'date-fns/isEqual';
import { startOfMonth } from 'date-fns/startOfMonth';
import isEqual from 'lodash/isEqual';
import { Middleware, shift } from '@floating-ui/react';

import { RANGE_PICKER } from '@nocowanie/core/consts';
import { dateHelpers } from '@nocowanie/core/helpers';

import './../date-picker/date-picker.scss';
import { defaultRangePickerConfig, RangePickerConfig } from './range-picker.config';
import { RangePickerDates, RangePickerProps } from './range-picker.props';
import { RangePickerDrawer } from './range-picker-drawer';
import { RangePickerFlexDates } from './range-picker-flex-dates';
import { RangePickerFooterContent } from './range-picker-footer-content';
import { RangePickerHeader } from './range-picker-header';
import { RangePickerPopper } from './range-picker-popper';
import { RangePickerTrigger } from './range-picker-trigger';
import { useRangePickerPopper } from './use-range-picker-popper.hook';

import { CommonModalsEnum, SessionStorageKeysEnum } from '../../../enums';
import { rangePickerHelpers } from '../../../helpers';
import { ensureElementVisible } from '../../../helpers/ensure-element-visible';
import { useFragmentActions, useModalSync, useSwipeGesture } from '../../../hooks';

const globalLocale = dateHelpers.getGlobalLocale();
registerLocale(globalLocale, dateHelpers.dateFnsLocales[globalLocale]);
setDefaultLocale(globalLocale);

const forceBottomPlacement = (): Middleware => ({
    name: 'force-bottom-placement',
    fn({ placement }) {
        if (placement.startsWith('top')) {
            return { reset: { placement: 'bottom' } };
        }

        return {};
    },
});

export const RangePicker = ({
    selectedDates,
    pickerProps,
    onRangeChange,
    config,
    daysConfig,
    children,
    onError,
    isMobileBrowser = true,
    inputProps = {},
    displayedDateFormat = 'dd MMM.',
    excludeDateStrings = [],
    isWebComponent,
    saveToSession,
    isClearable = false,
    flexDates,
    defaultIsOpen = false,
}: RangePickerProps) => {
    const recipe = useSlotRecipe({
        key: 'rangePicker',
    });
    const styles = recipe();
    const {
        withFlexDates = false,
        onFlexDateSelect,
        selectedFlexDays,
        hideFlexDaysLabel,
    } = flexDates || {};
    const isValidSelection = useCallback((dates: RangePickerDates): boolean => {
        const [start, end] = dates;
        return (
            (start === null && end === null) ||
            !(start === null || end === null || differenceInDays(end, start) < 1)
        );
    }, []);

    const initialState: { startDate: null | Date; endDate: null | Date } = {
        startDate: null,
        endDate: null,
    };

    if (selectedDates && isValidSelection([selectedDates[0], selectedDates[1]])) {
        initialState.startDate = selectedDates[0];
        initialState.endDate = selectedDates[1];
    }

    const calculateCurrentMonthDate = useCallback(
        (startDate: Date | undefined | null) =>
            startDate ? startOfMonth(startDate) : startOfMonth(new Date()),
        [],
    );
    const [componentState, setComponentState] = useState<{
        startDate: Date | null;
        endDate: Date | null;
    }>({
        ...initialState,
    });
    const [displayedRange, setDisplayedRange] = useState<{
        startDate: Date | null;
        endDate: Date | null;
    }>({
        ...initialState,
    });
    const [currentMonthDate, setCurrentMonthDate] = useState<Date | undefined>(
        calculateCurrentMonthDate(displayedRange.startDate),
    );
    const datePickerRef = useRef<DatePicker>(null);
    const withDrawer = isMobileBrowser;
    const [focusSelectedMonth, setFocusSelectedMonth] = useState<boolean>(false);
    const [monthsShown, setMonthsShown] = useState<number>(RANGE_PICKER.MONTHS_SHOWN);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [daysConfigCached, setDaysConfigCached] = useState(daysConfig ? { ...daysConfig } : null);
    const [isCalendarPending, setIsCalendarPending] = useState<boolean>(false);
    const [isInitialConfigLoaded, setIsInitialConfigLoaded] = useState(false);
    const originalFlexDaysRef = useRef<number>(selectedFlexDays ?? RANGE_PICKER.FLEX_DATES.DEFAULT);

    const { translationData }: Required<RangePickerConfig> = {
        ...defaultRangePickerConfig,
        ...config,
    };

    const locationId = useId();

    const { addFragment, removeFragment } = useFragmentActions(
        CommonModalsEnum.Calendar,
        locationId,
    );

    useEffect(() => {
        if (isEqual(daysConfig, daysConfigCached)) {
            return;
        }
        setDaysConfigCached(daysConfig ? { ...daysConfig } : null);
    }, [daysConfig]);

    useEffect(() => {
        if (!daysConfigCached || isInitialConfigLoaded) {
            return;
        }

        setIsInitialConfigLoaded(true);

        if (onError) {
            const errorMessage = rangePickerHelpers.getRangeErrorMessage(
                displayedRange?.startDate,
                displayedRange?.endDate,
                daysConfigCached,
                translationData,
            );
            onError(errorMessage);
        }
    }, [
        daysConfigCached,
        displayedRange?.startDate,
        displayedRange?.endDate,
        isInitialConfigLoaded,
    ]);

    const startDayConfig = rangePickerHelpers.getDayConfig(
        displayedRange?.startDate ?? null,
        daysConfigCached,
    );
    const isOnlyStartDateSelected = !!displayedRange?.startDate && !displayedRange?.endDate;
    const showFooterContent = !!(
        (isOnlyStartDateSelected &&
            (startDayConfig?.minStay || startDayConfig?.maxStay || startDayConfig?.minCutOff)) ||
        // We want to show the info only if currently selected dates match the dates from api response
        (inputProps?.invalid &&
            selectedDates?.[0] &&
            selectedDates?.[1] &&
            displayedRange?.startDate &&
            displayedRange?.endDate &&
            isEqualDate(selectedDates[0], displayedRange.startDate) &&
            isEqualDate(selectedDates[1], displayedRange.endDate))
    );

    const onCancel = useCallback(() => {
        if (isEqual(componentState, displayedRange)) {
            return;
        }

        setDisplayedRange({ ...componentState });
    }, [componentState, displayedRange]);

    const {
        open: isDrawerOpen,
        onOpen: onDrawerOpen,
        onClose: onDrawerClose,
    } = useDisclosure({
        onOpen: () => {
            originalFlexDaysRef.current = selectedFlexDays ?? RANGE_PICKER.FLEX_DATES.DEFAULT;
            setTimeout(() => {
                setIsCalendarPending(false);
            }, 0);
        },
        onClose: () => {
            setTimeout(() => {
                setIsCalendarPending(false);
                onPopperClose();
                removeFragment();

                setTimeout(() => {
                    setCurrentMonthDate(calculateCurrentMonthDate(displayedRange.startDate));
                }, 0);
            }, 0);
        },
    });

    const onDrawerCancel = useCallback(() => {
        if (withFlexDates) {
            onFlexDateSelect?.(originalFlexDaysRef.current);
        }

        setTimeout(() => {
            onCancel();
            onDrawerClose();
        }, 0);
    }, [onCancel, onDrawerClose, withFlexDates, onFlexDateSelect]);

    const onDrawerSubmit = useCallback(() => {
        if (!emitRangeChanged([displayedRange?.startDate, displayedRange?.endDate])) {
            return;
        }
        setComponentState({
            startDate: displayedRange?.startDate,
            endDate: displayedRange?.endDate,
        });
        !sessionStorage.getItem(SessionStorageKeysEnum.CriteriaChanged) &&
            sessionStorage.setItem(SessionStorageKeysEnum.CriteriaChanged, 'true');
        setTimeout(() => {
            onDrawerClose();
        }, 0);
    }, [onDrawerClose, displayedRange?.startDate, displayedRange?.endDate]);

    useEffect(() => {
        if (initialState && !isEqual(componentState, initialState)) {
            setComponentState({
                ...initialState,
            });
            onRangeChanged([initialState.startDate, initialState.endDate]);
        }
    }, [selectedDates]);

    useModalSync({
        modalName: CommonModalsEnum.Calendar,
        onOpen: onDrawerOpen,
        onClose: () => {
            onCancel();
            onDrawerClose();
        },
        isOpen: isDrawerOpen,
        fragmentId: locationId,
    });

    const emitRangeChanged = useCallback(
        (dates: RangePickerDates): boolean => {
            const [start, end] = dates;

            if (!isValidSelection(dates)) {
                return false;
            }

            setComponentState({
                startDate: start,
                endDate: end,
            });

            setTimeout(() => {
                onRangeChange && onRangeChange(dates);

                if (onError) {
                    const errorMessage = rangePickerHelpers.getRangeErrorMessage(
                        start,
                        end,
                        daysConfigCached,
                        translationData,
                    );
                    onError(errorMessage);
                }
            }, 0);

            return true;
        },
        [isValidSelection, onRangeChange, onError, daysConfigCached],
    );

    const onRangeChanged = useCallback(
        (
            dates: [Date | null, Date | null],
            event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | undefined,
        ): void => {
            const [start, end] = dates;
            setDisplayedRange({
                startDate: start,
                endDate: end,
            });

            if (!withDrawer) {
                if (isWebComponent) {
                    emitRangeChanged(dates);
                } else {
                    setIsCalendarPending(true);
                    emitRangeChanged(dates);
                    setTimeout(() => {
                        saveToSession &&
                            !sessionStorage.getItem(SessionStorageKeysEnum.CriteriaChanged) &&
                            sessionStorage.setItem(SessionStorageKeysEnum.CriteriaChanged, 'true');
                        setIsCalendarPending(false);
                    }, 0);
                }
            }
        },
        [emitRangeChanged, isWebComponent, saveToSession, withDrawer],
    );

    const renderDayContents = useCallback((dayOfMonth: number, date: Date) => {
        return (
            <span className={'react-datepicker__day-inner'} data-date={date.getTime()}>
                {dayOfMonth}
            </span>
        );
    }, []);

    const dayClassName = useCallback(
        (date: Date) => {
            return rangePickerHelpers.getDayClassName(
                date,
                displayedRange?.startDate ?? null,
                displayedRange?.endDate ?? null,
                daysConfigCached,
            );
        },
        [displayedRange?.startDate, displayedRange?.endDate, daysConfigCached],
    );

    const excludeDates = useMemo(() => {
        return rangePickerHelpers.getExcludedDates(
            excludeDateStrings ?? [],
            daysConfigCached,
            displayedRange?.startDate ?? null,
            displayedRange?.endDate ?? null,
        );
    }, [daysConfigCached, displayedRange?.startDate, displayedRange?.endDate, excludeDateStrings]);

    const filterDate = useCallback(
        (date: Date) => {
            const dateString = dateHelpers.format(date, RANGE_PICKER.DATE_FORMAT);

            return !excludeDates?.includes(dateString);
        },
        [excludeDates],
    );

    const {
        onDatepickerEvent,
        referenceBounds,
        isPopoverOpen,
        tooltipText,
        onClose: onPopperClose,
    } = useRangePickerPopper({
        daysConfig: daysConfigCached,
        isMobile: isMobileBrowser,
        translationData,
        dateRange: displayedRange,
    });

    const onClear = useCallback(() => {
        setTimeout(() => {
            onPopperClose();
            onRangeChanged([null, null]);
        }, 0);
    }, [onPopperClose, onRangeChanged]);

    const CalendarFooterContent = () =>
        showFooterContent ? (
            <RangePickerFooterContent
                startDate={displayedRange?.startDate}
                endDate={displayedRange?.endDate}
                isInvalid={inputProps?.invalid}
                translationData={translationData}
                displayedDateFormat={displayedDateFormat}
                daysConfig={daysConfigCached}
            />
        ) : null;

    const RangePickerContainer = ({
        className,
        children,
        showPopperArrow,
        arrowProps = {},
        datePickerRef,
    }: DatePickerProps & {
        arrowProps: Record<string, any>;
        datePickerRef: React.RefObject<DatePicker>;
    }) => {
        const calendarInstance = datePickerRef.current?.calendar;
        const monthNavigationState = useMemo(() => {
            const currentMonth = new Date();
            const minMonth = startOfMonth(currentMonth);
            const maxMonth = startOfMonth(addMonths(currentMonth, 11));
            const displayedMonth = calendarInstance?.state;

            if (!displayedMonth) return { isPrevMonthDisabled: false, isNextMonthDisabled: false };

            return {
                isPrevMonthDisabled: startOfMonth(displayedMonth.date) <= minMonth,
                isNextMonthDisabled: startOfMonth(displayedMonth.date) >= maxMonth,
            };
        }, [calendarInstance?.state]);

        const handleSwipeDown = useCallback(() => {
            onPopperClose();
            if (!monthNavigationState.isPrevMonthDisabled) {
                requestAnimationFrame(() => calendarInstance?.decreaseMonth());
            }
        }, [calendarInstance, monthNavigationState.isPrevMonthDisabled]);

        const handleSwipeUp = useCallback(() => {
            onPopperClose();
            if (!monthNavigationState.isNextMonthDisabled) {
                requestAnimationFrame(() => calendarInstance?.increaseMonth());
            }
        }, [calendarInstance, monthNavigationState.isNextMonthDisabled]);

        const { handleTouchStart, handleTouchEnd } = useSwipeGesture({
            onSwipeDown: handleSwipeDown,
            onSwipeUp: handleSwipeUp,
        });

        const showClearButton =
            isClearable && (displayedRange?.startDate || displayedRange?.endDate);

        useEffect(() => {
            if (!isDrawerOpen || !isMobileBrowser) {
                return;
            }

            bodyRef.current?.addEventListener('touchstart', handleTouchStart);
            bodyRef.current?.addEventListener('touchend', handleTouchEnd);

            return () => {
                bodyRef.current?.removeEventListener('touchstart', handleTouchStart);
                bodyRef.current?.removeEventListener('touchend', handleTouchEnd);
            };
        }, [handleTouchEnd, handleTouchStart]);

        return (
            <CalendarContainer className={className}>
                <Box
                    ref={containerRef}
                    css={styles.box}
                    onMouseMove={
                        !isMobileBrowser && daysConfigCached ? onDatepickerEvent : undefined
                    }
                    onMouseLeave={
                        !isMobileBrowser && daysConfigCached ? onDatepickerEvent : undefined
                    }
                    onClick={isMobileBrowser && daysConfigCached ? onDatepickerEvent : undefined}
                >
                    {showPopperArrow ? (
                        <div className="react-datepicker__triangle" {...arrowProps} />
                    ) : null}
                    <Box
                        css={{
                            position: 'relative',
                            _after: {
                                content: '""',
                                display: 'table',
                                clear: 'both',
                            },
                        }}
                    >
                        {children}
                    </Box>
                    {(showFooterContent || showClearButton) && !isMobileBrowser ? (
                        <HStack px={3.5} pb={3} justify={'space-between'} gap={4} width={'100%'}>
                            {showFooterContent ? (
                                <Box maxW={'380px'}>
                                    <CalendarFooterContent />
                                </Box>
                            ) : null}

                            {showClearButton ? (
                                <Link as={'button'} ml={'auto'} onClick={onClear} fontSize={'md'}>
                                    {translationData.clearLabel}
                                </Link>
                            ) : null}
                        </HStack>
                    ) : null}
                    {withFlexDates && !isMobileBrowser ? (
                        <Box mx={6} py={5} borderTop={'1px solid'} borderTopColor={'gray.200'}>
                            <RangePickerFlexDates
                                translationData={translationData}
                                onFlexDateSelect={onFlexDateSelect}
                                selectedFlexDays={selectedFlexDays}
                            />
                        </Box>
                    ) : null}
                </Box>
            </CalendarContainer>
        );
    };

    const onMonthChange = useCallback((date: Date) => {
        // Rerender after swipe does not remember shown month correctly so we need to set it manually
        setCurrentMonthDate(date);
    }, []);

    const containerRef = useRef<HTMLDivElement | null>(null);

    const formatWeekDay = useCallback((polishDay: string) => {
        const dayOfWeek = rangePickerHelpers.getNumericDayOfWeek(polishDay);
        return translationData.weekDayNames[dayOfWeek];
    }, []);

    // There is weird type issue after picker's update - it needs types (onChange, selectsRange, selectsMultiple) explicitly set
    // https://github.com/Hacker0x01/react-datepicker/issues/4924
    // https://github.com/Hacker0x01/react-datepicker/issues/4999
    const commonPickerProps: Omit<
        DatePickerProps,
        'onChange' | 'excludeDates' | 'selectsRange' | 'selectsMultiple' | 'showMonthYearDropdown'
    > & {
        onChange: (
            dates: [Date | null, Date | null],
            event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | undefined,
        ) => void;
        selectsRange: true;
        ref: (picker?: any) => void;
        selectsMultiple?: never;
    } = {
        ...pickerProps,
        startDate: displayedRange?.startDate ?? undefined,
        endDate: displayedRange?.endDate ?? undefined,
        onChange: onRangeChanged,
        minDate: dateHelpers.getMinRangeDate(
            displayedRange?.startDate ?? null,
            displayedRange?.endDate ?? null,
            startDayConfig?.minStay,
        ),
        maxDate: dateHelpers.getMaxRangeDate(
            displayedRange?.startDate ?? null,
            displayedRange?.endDate ?? null,
            startDayConfig?.maxStay,
        ),
        selectsRange: true,
        selectsMultiple: undefined,
        todayButton: null,
        calendarContainer: calendarProps => (
            <RangePickerContainer {...calendarProps} datePickerRef={datePickerRef} />
        ),
        dayClassName,
        renderDayContents,
        filterDate: isOpen || isDrawerOpen ? filterDate : undefined, // Do not use excludeDates! it's poorly optimized
        ref: (picker: any) => {
            // https://github.com/Hacker0x01/react-datepicker/issues/1480
            // React-datepicker overrides input's readonly prop, so we need to override it >again<
            if (picker && picker.input) {
                picker.input.readOnly = true;

                // Some other props are not passed too
                for (const [propName, propValue] of Object.entries(inputProps as object)) {
                    picker.input[propName] = propValue;
                }
            }
            (datePickerRef as React.MutableRefObject<DatePicker>).current = picker;
        },
    };

    const handleTriggerClick = () => {
        if (withDrawer) {
            setCurrentMonthDate(calculateCurrentMonthDate(displayedRange.startDate));
            setIsCalendarPending(true);
            setTimeout(() => {
                onDrawerOpen();
            }, 0);
            addFragment();
        } else {
            setIsOpen(true);
        }
    };

    const hasAutoOpenedRef = useRef(false);

    useEffect(() => {
        if (defaultIsOpen && !hasAutoOpenedRef.current) {
            hasAutoOpenedRef.current = true;
            setIsOpen(true);
        }
    }, [defaultIsOpen]);

    const commonTriggerProps = {
        startDate: displayedRange.startDate,
        endDate: displayedRange.endDate,
        onClick: handleTriggerClick,
        inputProps,
        isLoading: isCalendarPending,
        flexDates: {
            withFlexDates,
            selectedFlexDays,
            hideFlexDaysLabel,
        },
        children,
    };

    const DatePickerMobile = memo(() => {
        return (
            <DatePicker
                inline={true}
                calendarClassName={'react-datepicker--drawer'}
                renderCustomHeader={({ ...props }) => <RangePickerHeader isMobile {...props} />}
                openToDate={currentMonthDate}
                focusSelectedMonth={false}
                monthsShown={RANGE_PICKER.MONTHS_SHOWN}
                onMonthChange={onMonthChange}
                {...commonPickerProps}
            />
        );
    });

    const bodyRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        bodyRef.current = document.body;
    }, []);

    return (
        <>
            {withDrawer ? (
                <>
                    <RangePickerTrigger withDrawer {...commonTriggerProps} />
                    <RangePickerDrawer
                        open={isDrawerOpen}
                        translationData={translationData}
                        onOpenChange={details => {
                            setTimeout(() => (details.open ? undefined : onDrawerCancel()), 0);
                        }}
                        onSubmit={onDrawerSubmit}
                        startDate={displayedRange?.startDate}
                        endDate={displayedRange?.endDate}
                        footerContent={showFooterContent ? <CalendarFooterContent /> : null}
                        isLoading={isCalendarPending}
                        setIsLoading={setIsCalendarPending}
                        isClearable={isClearable}
                        onClear={onClear}
                        withFlexDates={withFlexDates}
                        onFlexDateSelect={onFlexDateSelect}
                        selectedFlexDays={selectedFlexDays}
                    >
                        <DatePickerMobile />
                    </RangePickerDrawer>
                </>
            ) : (
                <>
                    {hasAutoOpenedRef.current &&
                        bodyRef.current &&
                        ReactDOM.createPortal(
                            <div
                                style={{
                                    position: 'fixed',
                                    inset: 0,
                                    zIndex: 1300,
                                    background: 'rgba(22, 34, 42, .5)',
                                }}
                            />,
                            bodyRef.current,
                        )}
                    <DatePicker
                        onInputClick={() => (!isOpen ? setIsCalendarPending(true) : undefined)}
                        open={isOpen}
                        onClickOutside={() => {
                            setIsOpen(false);
                            hasAutoOpenedRef.current = false;
                        }}
                        onCalendarOpen={() => {
                            requestAnimationFrame(() => {
                                setIsOpen(true);
                                // focusSelectedMonth + monthsShown change - fix for shifting months issue
                                // https://github.com/Hacker0x01/react-datepicker/issues/3942
                                setFocusSelectedMonth(false);
                                setIsCalendarPending(false);
                                ensureElementVisible('.react-datepicker-popper');
                            });
                        }}
                        onCalendarClose={() => {
                            setIsCalendarPending(true);
                            setTimeout(() => {
                                onPopperClose();
                                setIsOpen(false);
                                setFocusSelectedMonth(true);
                                setMonthsShown(1);
                                setTimeout(() => {
                                    setMonthsShown(RANGE_PICKER.MONTHS_SHOWN);
                                }, 0);
                                onCancel();

                                setIsCalendarPending(false);
                            }, 0);
                        }}
                        focusSelectedMonth={focusSelectedMonth}
                        monthsShown={monthsShown}
                        shouldCloseOnSelect={!withFlexDates}
                        popperPlacement={'bottom'}
                        customInput={<RangePickerTrigger {...commonTriggerProps} />}
                        popperContainer={
                            isWebComponent
                                ? ({ children }) =>
                                      bodyRef.current &&
                                      ReactDOM.createPortal(
                                          // eslint-disable-next-line react/jsx-no-useless-fragment
                                          <>{children}</>,
                                          bodyRef.current,
                                      )
                                : undefined
                        }
                        popperModifiers={[
                            shift(),
                            // Override `flip` middleware behavior: https://github.com/Hacker0x01/react-datepicker/blob/v7.5.0/src/with_floating.tsx#L56-L60
                            forceBottomPlacement(),
                        ]}
                        formatWeekDay={formatWeekDay}
                        renderCustomHeader={({ ...props }) => <RangePickerHeader {...props} />}
                        {...commonPickerProps}
                    />
                </>
            )}
            {daysConfigCached && (isOpen || isDrawerOpen) ? (
                <RangePickerPopper
                    isMobile={isMobileBrowser}
                    onClose={onPopperClose}
                    translationData={translationData}
                    referenceBounds={referenceBounds}
                    isPopoverOpen={isPopoverOpen}
                    tooltipText={tooltipText}
                />
            ) : null}
        </>
    );
};
